import { defineStore } from 'pinia'
import { ref } from 'vue'
import { ElMessage } from 'element-plus'
import * as golfApi from '@/api/golf'
import { extractErrorMessage } from '@/helpers/error-parsers'
import type {
  GolfTrip,
  GolfTransaction,
  FinishGolfPaymentPayload,
  GolfTripDiscount,
} from '@/api/golf'

export const useGolfTripsStore = defineStore('golf-trips', () => {
  const trips = ref<GolfTrip[]>([])
  const transactions = ref<GolfTransaction[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const fetchTrips = async (filters?: {
    status?: string
    pricing_type?: string
    payment_method?: string
    page?: number
    per_page?: number
  }) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.getGolfTrips(filters)
      const responseData = response.data.data as any
      if (responseData && responseData.data && Array.isArray(responseData.data)) {
        trips.value = responseData.data
      } else if (Array.isArray(responseData)) {
        trips.value = responseData
      } else {
        trips.value = []
      }
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to fetch trips')
      console.error('Error fetching trips:', e)
      trips.value = []
    } finally {
      isLoading.value = false
    }
  }

  const fetchTrip = async (id: string) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.getGolfTrip(id)
      const responseData = response.data.data as any
      if (responseData && responseData.data) {
        return responseData.data
      }
      return responseData || null
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to fetch trip')
      console.error('Error fetching trip:', e)
      return null
    } finally {
      isLoading.value = false
    }
  }

  const approveTrip = async (id: string) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.approveGolfTrip(id)
      const updated = response.data.data
      if (updated) {
        const index = trips.value.findIndex(t => t.id === id)
        if (index !== -1) {
          trips.value[index] = { ...trips.value[index], ...updated }
        }
        ElMessage.success('Trip approved successfully')
        return updated
      }
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to approve trip')
      ElMessage.error({
        message: error.value,
        duration: 6000,
        showClose: true,
      })
      console.error('Error approving trip:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  const fetchTripDiscounts = async (id: string) => {
    try {
      const response = await golfApi.getGolfTripDiscounts(id)
      const discounts = (response.data.data as any)?.discounts || []
      return discounts as GolfTripDiscount[]
    } catch (e: any) {
      console.error('Error fetching trip discounts:', e)
      ElMessage.error({
        message: extractErrorMessage(e, 'Failed to load discounts'),
        duration: 6000,
        showClose: true,
      })
      return []
    }
  }

  const removeTripDiscount = async (tripId: string, discountId: string) => {
    try {
      await golfApi.removeGolfTripDiscount(tripId, discountId)
      ElMessage.success('Discount removed successfully')
      return true
    } catch (e: any) {
      console.error('Error removing trip discount:', e)
      ElMessage.error({
        message: extractErrorMessage(e, 'Failed to remove discount'),
        duration: 6000,
        showClose: true,
      })
      return false
    }
  }

  const declineTrip = async (id: string, comment?: string) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.declineGolfTrip(id, comment)
      const updated = response.data.data
      if (updated) {
        const index = trips.value.findIndex(t => t.id === id)
        if (index !== -1) {
          trips.value[index] = { ...trips.value[index], ...updated }
        }
        ElMessage.success('Trip declined successfully')
        return updated
      }
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to decline trip')
      ElMessage.error({
        message: error.value,
        duration: 6000,
        showClose: true,
      })
      console.error('Error declining trip:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  const completeTrip = async (id: string) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.completeGolfTrip(id)
      const updated = response.data.data
      if (updated) {
        const index = trips.value.findIndex(t => t.id === id)
        if (index !== -1) {
          trips.value[index] = { ...trips.value[index], ...updated }
        }
        ElMessage.success('Trip completed successfully')
        return updated
      }
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to complete trip')
      ElMessage.error({
        message: error.value,
        duration: 6000,
        showClose: true,
      })
      console.error('Error completing trip:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  const endTrip = async (id: string) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.endGolfTrip(id)
      const updated = response.data.data
      if (updated) {
        const index = trips.value.findIndex(t => t.id === id)
        if (index !== -1) {
          trips.value[index] = { ...trips.value[index], ...updated }
        }
        ElMessage.success('Trip ended successfully')
        return updated
      }
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to end trip')
      ElMessage.error({
        message: error.value,
        duration: 6000,
        showClose: true,
      })
      console.error('Error ending trip:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  const cancelTrip = async (id: string, comment?: string) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.cancelGolfTrip(id, comment)
      const updated = response.data.data
      if (updated) {
        const index = trips.value.findIndex(t => t.id === id)
        if (index !== -1) {
          trips.value[index] = { ...trips.value[index], ...updated }
        }
        ElMessage.success('Trip cancelled successfully')
        return updated
      }
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to cancel trip')
      ElMessage.error({
        message: error.value,
        duration: 6000,
        showClose: true,
      })
      console.error('Error cancelling trip:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  const holdTrip = async (id: string, reason?: string) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.holdGolfTrip(id, reason)
      const updated = response.data.data
      if (updated) {
        const index = trips.value.findIndex(t => t.id === id)
        if (index !== -1) {
          trips.value[index] = { ...trips.value[index], ...updated }
        }
        ElMessage.success('Trip put on hold successfully')
        return updated
      }
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to hold trip')
      ElMessage.error({
        message: error.value,
        duration: 6000,
        showClose: true,
      })
      console.error('Error holding trip:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  const unholdTrip = async (id: string) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.unholdGolfTrip(id)
      const updated = response.data.data
      if (updated) {
        const index = trips.value.findIndex(t => t.id === id)
        if (index !== -1) {
          trips.value[index] = { ...trips.value[index], ...updated }
        }
        ElMessage.success('Trip released from hold successfully')
        return updated
      }
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to unhold trip')
      ElMessage.error({
        message: error.value,
        duration: 6000,
        showClose: true,
      })
      console.error('Error unholding trip:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  const discountTrip = async (id: string, amount: number, reason?: string) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.discountGolfTrip(id, amount, reason)
      const updated = response.data.data
      if (updated) {
        const index = trips.value.findIndex(t => t.id === id)
        if (index !== -1) {
          trips.value[index] = { ...trips.value[index], ...updated }
        }
        ElMessage.success('Discount applied successfully')
        return updated
      }
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to apply discount')
      ElMessage.error({
        message: error.value,
        duration: 6000,
        showClose: true,
      })
      console.error('Error applying discount:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  const changeTripAsset = async (id: string, assetId: string) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.changeTripAsset(id, assetId)
      const updated = response.data.data
      if (updated) {
        const index = trips.value.findIndex(t => t.id === id)
        if (index !== -1) {
          trips.value[index] = { ...trips.value[index], ...updated }
        }
        ElMessage.success('Asset changed successfully')
        return updated
      }
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to change asset')
      ElMessage.error({
        message: error.value,
        duration: 6000,
        showClose: true,
      })
      console.error('Error changing asset:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  const finishPayment = async (id: string, payload: FinishGolfPaymentPayload) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.finishGolfPayment(id, payload)
      const result = response.data.data
      ElMessage.success(result?.message || 'Payment operation completed')
      return result
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to process payment')
      ElMessage.error({
        message: error.value,
        duration: 6000,
        showClose: true,
      })
      console.error('Error processing payment:', e)
      return null
    } finally {
      isLoading.value = false
    }
  }

  const fetchTransactions = async (params?: {
    trip_id?: string
    user_id?: string
    action?: string
    type?: string
    page?: number
    per_page?: number
  }) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.getGolfTransactions(params)
      const responseData = response.data.data as any
      if (responseData && responseData.data && Array.isArray(responseData.data)) {
        transactions.value = responseData.data
      } else if (Array.isArray(responseData)) {
        transactions.value = responseData
      } else {
        transactions.value = []
      }
      return transactions.value
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to fetch transactions')
      console.error('Error fetching transactions:', e)
      transactions.value = []
      return []
    } finally {
      isLoading.value = false
    }
  }

  const fetchReserves = async (params?: {
    status?: string
    pricing_type?: string
    payment_method?: string
    profile_id?: string
    asset_id?: string
  }) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.getGolfReserves(params)
      const responseData = response.data.data as any
      if (responseData && responseData.data && Array.isArray(responseData.data)) {
        trips.value = responseData.data
      } else if (Array.isArray(responseData)) {
        trips.value = responseData
      } else {
        trips.value = []
      }
      return trips.value
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to fetch reserves')
      console.error('Error fetching reserves:', e)
      trips.value = []
      return []
    } finally {
      isLoading.value = false
    }
  }

  const resetError = () => {
    error.value = null
  }

  return {
    trips,
    transactions,
    isLoading,
    error,
    fetchTrips,
    fetchTrip,
    approveTrip,
    declineTrip,
    completeTrip,
    endTrip,
    cancelTrip,
    holdTrip,
    unholdTrip,
    discountTrip,
    fetchTripDiscounts,
    removeTripDiscount,
    changeTripAsset,
    finishPayment,
    fetchTransactions,
    fetchReserves,
    resetError,
  }
})
